import {
    useEffect,
    useMemo,
    type FC
} from "react";
import {
    Animated,
    View
} from "react-native";
import type IMessageScrollerButtonProps from "./type";
import stylesheet, {
    MESSAGE_SCROLLER_BUTTON_LABEL_KEYS,
    useStyles
} from "./stylesheet";
import {
    MESSAGE_SCROLLER_BUTTON_ICONS
} from "./icons";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    getSlideStyle,
    usePresence
} from "../../../../helpers/motion";
import {
    useMessageScrollerRootContext,
    useMessageScrollerSelector,
    useMessageScrollerContext
} from "../../context";
import {
    formatNumber
} from "../../../../helpers/locale";
import {
    warnOnce
} from "../../../../utils/development";
import Button from "../../../button";

const MessageScrollerButtonContent: FC<IMessageScrollerButtonProps> = ({
    newMessageCount: newMessageCountProp,
    direction = "end",
    customLocalize,
    customTheme,
    style,
    ...props
}) => {
    const context = useMessageScrollerContext();

    const rootContext = useMessageScrollerRootContext();

    const currentTheme = customTheme ?? rootContext?.customTheme;

    const {
        shapeConfigs,
        shadowAlpha,
        elevations,
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(currentTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize ?? rootContext?.customLocalize);

    const isAtBoundary = useMessageScrollerSelector(context, (snapshot) => direction === "end" ? snapshot.isAtEnd : snapshot.isAtStart, true);

    const storeCount = useMessageScrollerSelector(context, (snapshot) => snapshot.newMessageCount, 0);

    const isLoadingOlder = useMessageScrollerSelector(context, (snapshot) => snapshot.isLoadingOlder, false);

    const newMessageCount = direction === "end" ? newMessageCountProp ?? storeCount : 0;

    const isActive = (!isAtBoundary || newMessageCount > 0) && !(direction === "start" && isLoadingOlder);

    const {
        isReduceMotionEnabled,
        isRendered,
        progress
    } = usePresence({
        enterIntent: "surfaceEnter",
        customTheme: currentTheme,
        exitIntent: "surfaceExit",
        isVisible: isActive
    });

    const slideStyle = useMemo(() => getSlideStyle({
        offset: spaces.spacingSm * (direction === "start" ? -1 : 1),
        isReduceMotionEnabled,
        progress
    }), [
        isReduceMotionEnabled,
        spaces.spacingSm,
        direction,
        progress
    ]);

    const {
        container: containerDynamicStyle,
        surface: surfaceDynamicStyle
    } = useStyles({
        shapeConfigs,
        shadowAlpha,
        elevations,
        direction,
        radiuses,
        colors,
        spaces
    });

    useEffect(() => {
        if(context) {
            return;
        }

        warnOnce({
            message: "MessageScroller.Button: it must be rendered inside a MessageScroller.Provider."
        });
    }, [
        context
    ]);

    const handlePress = () => {
        if(direction === "end") {
            context?.commands.scrollToEnd();

            return;
        }

        context?.commands.scrollToStart();
    };

    if(!isRendered) {
        return null;
    }

    const label = localize(MESSAGE_SCROLLER_BUTTON_LABEL_KEYS[direction]);

    const countLabel = localize("x-new-messages", [
        formatNumber({
            value: newMessageCount,
            locale: activeLocale
        })
    ]);

    return <Animated.View
        {...props}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle,
            slideStyle
        ]}
    >
        <View
            style={[
                stylesheet.surface,
                surfaceDynamicStyle
            ]}
        >
            <Button
                accessibilityLabel={newMessageCount > 0 ? countLabel : label}
                title={newMessageCount > 0 ? countLabel : undefined}
                icon={MESSAGE_SCROLLER_BUTTON_ICONS[direction]}
                customTheme={currentTheme}
                onPress={handlePress}
                variant="outline"
                type="neutral"
                size="small"
            />
        </View>
    </Animated.View>;
};

const MessageScrollerButton: FC<IMessageScrollerButtonProps> = (props) => {
    const context = useMessageScrollerContext();

    const isMeasured = useMessageScrollerSelector(context, (snapshot) => snapshot.isMeasured, false);

    if(context && !isMeasured) {
        return null;
    }

    return <MessageScrollerButtonContent
        {...props}
    />;
};
export default MessageScrollerButton;
